import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Trash2, Plus, MapPin, Building2, Users } from "lucide-react";
import { queryDocs } from "@/integrations/firebase/data";
import { saveBranch, saveDepartment, saveEmployee, deleteEntity } from "@/integrations/firebase/admin-ops";
import { PickDealership } from "@/components/pick-dealership";

export const Route = createFileRoute("/_app/branches")({
  head: () => ({ meta: [{ title: "Branches — EESWEB" }] }),
  component: BranchesPage,
});

function BranchesPage() {
  const { role, loading, effectiveClientId } = useAuth();
  const qc = useQueryClient();

  const clientId = effectiveClientId;

  const { data: branches } = useQuery({
    queryKey: ["cfg-branches", clientId],
    enabled: !!clientId,
    queryFn: async () => { const r = await queryDocs<any>("branches", { clientId: clientId! }); return r.sort((a,b)=>(a.sort_order??0)-(b.sort_order??0)); },
  });
  const { data: departments } = useQuery({
    queryKey: ["cfg-depts", clientId],
    enabled: !!clientId,
    queryFn: async () => { const r = await queryDocs<any>("departments", { clientId: clientId! }); return r.sort((a,b)=>(a.sort_order??0)-(b.sort_order??0)); },
  });
  const { data: employees } = useQuery({
    queryKey: ["cfg-emps", clientId],
    enabled: !!clientId,
    queryFn: async () => { const r = await queryDocs<any>("employees", { clientId: clientId! }); return r.sort((a,b)=>String(a.name).localeCompare(String(b.name))); },
  });

  if (loading) return null;
  if (role !== "admin") return <Navigate to="/dashboard" replace />;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["cfg-branches"] });
    qc.invalidateQueries({ queryKey: ["cfg-depts"] });
    qc.invalidateQueries({ queryKey: ["cfg-emps"] });
    qc.invalidateQueries({ queryKey: ["ivr-branches"] });
    qc.invalidateQueries({ queryKey: ["ivr-depts"] });
    qc.invalidateQueries({ queryKey: ["ivr-emps"] });
  };

  if (!clientId) return <PickDealership title="Branches" purpose="manage its branches, departments and employees" />;

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Branches &amp; teams</h1>
        <p className="text-sm text-muted-foreground">
          Manage the locations, teams and people that IVR options route calls to. Changes are
          used to label calls and drive dashboards — this panel monitors routing, it does not
          reprogram your phone system.
        </p>
      </div>

      <Tabs defaultValue="branches">
        <TabsList>
          <TabsTrigger value="branches"><MapPin className="size-3.5 mr-1.5" /> Branches</TabsTrigger>
          <TabsTrigger value="departments"><Building2 className="size-3.5 mr-1.5" /> Departments</TabsTrigger>
          <TabsTrigger value="employees"><Users className="size-3.5 mr-1.5" /> Employees</TabsTrigger>
        </TabsList>

        <TabsContent value="branches">
          <BranchEditor clientId={clientId} branches={branches ?? []} onChange={invalidate} />
        </TabsContent>
        <TabsContent value="departments">
          <DepartmentEditor clientId={clientId} departments={departments ?? []} onChange={invalidate} />
        </TabsContent>
        <TabsContent value="employees">
          <EmployeeEditor
            clientId={clientId}
            employees={employees ?? []}
            branches={branches ?? []}
            departments={departments ?? []}
            onChange={invalidate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BranchEditor({ clientId, branches, onChange }: { clientId: string; branches: any[]; onChange: () => void }) {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return toast.error("Enter a branch name");
    try {
      await saveBranch({ clientId, name: name.trim(), sortOrder: branches.length + 1 });
      setName("");
      onChange();
      toast.success("Branch added");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };
  const remove = async (id: string) => {
    try { await deleteEntity({ table: "branches", id }); onChange(); toast.success("Removed"); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Failed"); }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Branches</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-end">
          <div className="flex-1"><Label>New branch name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Kothrud" /></div>
          <Button onClick={add}><Plus className="size-4 mr-1" /> Add</Button>
        </div>
        <div className="divide-y divide-border border border-border rounded-md">
          {branches.map((b) => (
            <div key={b.id} className="flex items-center gap-2 p-2.5 text-sm">
              <MapPin className="size-3.5 text-muted-foreground" />
              <span className="font-medium">{b.name}</span>
              <Button size="icon" variant="ghost" className="ml-auto h-7 w-7" onClick={() => remove(b.id)}>
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
          {branches.length === 0 && <div className="p-3 text-sm text-muted-foreground">No branches yet.</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function DepartmentEditor({ clientId, departments, onChange }: { clientId: string; departments: any[]; onChange: () => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState("service");
  const add = async () => {
    if (!name.trim()) return toast.error("Enter a department name");
    try {
      await saveDepartment({ clientId, name: name.trim(), kind, sortOrder: departments.length + 1 });
      setName("");
      onChange();
      toast.success("Department added");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };
  const remove = async (id: string) => {
    try { await deleteEntity({ table: "departments", id }); onChange(); toast.success("Removed"); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Failed"); }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Departments</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[160px]"><Label>New department name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Service Appointment" /></div>
          <div className="w-40">
            <Label>Kind</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="service">Service</SelectItem>
                <SelectItem value="complaint">Complaint</SelectItem>
                <SelectItem value="hr">HR / Careers</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={add}><Plus className="size-4 mr-1" /> Add</Button>
        </div>
        <div className="divide-y divide-border border border-border rounded-md">
          {departments.map((d) => (
            <div key={d.id} className="flex items-center gap-2 p-2.5 text-sm">
              <Building2 className="size-3.5 text-muted-foreground" />
              <span className="font-medium">{d.name}</span>
              <span className="text-xs text-muted-foreground capitalize">({d.kind})</span>
              <Button size="icon" variant="ghost" className="ml-auto h-7 w-7" onClick={() => remove(d.id)}>
                <Trash2 className="size-3.5" />
              </Button>
            </div>
          ))}
          {departments.length === 0 && <div className="p-3 text-sm text-muted-foreground">No departments yet.</div>}
        </div>
      </CardContent>
    </Card>
  );
}

function EmployeeEditor({
  clientId, employees, branches, departments, onChange,
}: { clientId: string; employees: any[]; branches: any[]; departments: any[]; onChange: () => void }) {
  const [form, setForm] = useState({ name: "", mobile: "", branchId: "", departmentId: "" });
  const branchMap = new Map(branches.map((b) => [b.id, b.name]));
  const deptMap = new Map(departments.map((d) => [d.id, d.name]));
  const add = async () => {
    if (!form.name.trim()) return toast.error("Enter an employee name");
    try {
      await saveEmployee({
        clientId, name: form.name.trim(), mobile: form.mobile.trim() || null,
        branchId: form.branchId || null, departmentId: form.departmentId || null,
      });
      setForm({ name: "", mobile: "", branchId: "", departmentId: "" });
      onChange();
      toast.success("Employee added");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  };
  const remove = async (id: string) => {
    try { await deleteEntity({ table: "employees", id }); onChange(); toast.success("Removed"); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Failed"); }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Employees</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-2">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" /></div>
          <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Routing / contact number" /></div>
          <div>
            <Label>Branch</Label>
            <Select value={form.branchId || "none"} onValueChange={(v) => setForm({ ...form, branchId: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Department</Label>
            <Select value={form.departmentId || "none"} onValueChange={(v) => setForm({ ...form, departmentId: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={add}><Plus className="size-4 mr-1" /> Add employee</Button>
        <ul className="divide-y rounded-xl border">
          {employees.map((e) => (
            <li key={e.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="font-medium">{e.name}</span>
                  <span className="text-xs text-muted-foreground tabular">{e.mobile ?? "No number"}</span>
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {[e.branch_id && branchMap.get(e.branch_id), e.department_id && deptMap.get(e.department_id)].filter(Boolean).join(" · ") || "No branch or department"}
                </div>
              </div>
              <Button size="icon" variant="ghost" className="size-8 shrink-0 hover:text-destructive" aria-label={`Remove ${e.name}`} title="Remove" onClick={() => remove(e.id)}><Trash2 className="size-3.5" /></Button>
            </li>
          ))}
          {employees.length === 0 && <li className="px-3 py-4 text-sm text-muted-foreground">No employees yet.</li>}
        </ul>
      </CardContent>
    </Card>
  );
}
